import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DeleteConfirmDialog } from '@components/DeleteConfirmDialog';
import { useSubjectLanguageVariants } from '@features/user';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Chip, CircularProgress, Skeleton, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { useSnackbar } from 'notistack';

import { CLAIM_STATUS_OPTIONS, type ClaimStatus } from '../../constants/statusOptions';
import { useBillingClaims, useClaimSubmission, useClaimSubmit, useLazyGetClaimPdfQuery } from '../../store';

import { BillingTable } from './BillingTable';
import { ClaimAnnotationIndicator } from './ClaimAnnotationIndicator';
import type { Claim, ColumnDefinition, TableAction } from './types';

// Map UI field names to API sort field names
const SORT_FIELD_MAP: Record<
  string,
  'created_at' | 'service_date_from' | 'total_charge_cents'
> = {
  claimNumber: 'created_at', // claim_number not in API, using created_at as fallback
  dateOfService: 'service_date_from',
  dateCreated: 'created_at',
  amount: 'total_charge_cents',
  // Only these fields are sortable
};

interface ClaimsTabProps {
  filters: {
    patient?: string;
    from?: string;
    to?: string;
    status?: string;
  };
  sort?: string;
  order: 'asc' | 'desc';
  page: number;
  perPage: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  onSort: (field: string) => void;
  onSelectedClaimsChange?: (claims: Claim[]) => void;
}

export const ClaimsTab: React.FC<ClaimsTabProps> = ({
  filters,
  sort,
  order,
  page,
  perPage,
  onPageChange,
  onPerPageChange,
  onSort,
  onSelectedClaimsChange,
}) => {
  // Hooks
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [getClaimPdf] = useLazyGetClaimPdfQuery();
  const { deleteClaim } = useClaimSubmission();
  const { submitClaim, isSubmitting } = useClaimSubmit();
  const [downloadingClaimIds, setDownloadingClaimIds] = useState<Set<string>>(new Set());
  const subjectLanguage = useSubjectLanguageVariants();

  // State for delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [claimToDelete, setClaimToDelete] = useState<Claim | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [submittingClaimId, setSubmittingClaimId] = useState<string | null>(null);

  // Map sort field to API field name
  const apiSortBy = sort && SORT_FIELD_MAP[sort] ? SORT_FIELD_MAP[sort] : undefined;

  // Direct status filter to API parameter
  const claimStatus = filters.status as ClaimStatus | undefined;

  // Track if we need polling (initially check if status filter is processing)
  const [needsPolling, setNeedsPolling] = useState(false);

  // Fetch claims from API with conditional polling
  const { claims, totalCount, isLoading, error, refetch } = useBillingClaims(
    {
      patientId: filters.patient,
      status: claimStatus,
      dateFrom: filters.from,
      dateTo: filters.to,
      page,
      pageSize: perPage,
      sortBy: apiSortBy,
      sortOrder: apiSortBy ? order : undefined,
    },
    needsPolling ? 2000 : undefined // Poll every 2 seconds when processing claims exist
  );

  // Map API data to display format
  const mappedData: Claim[] = useMemo(
    () =>
      claims.map(claim => ({
        id: claim.id || '',
        claimNumber: claim.id || '', // Using ID as claim number - kept for PDF naming
        patientName: claim.patient_first_name && claim.patient_last_name 
          ? `${claim.patient_first_name}${claim.patient_middle_initial ? ` ${claim.patient_middle_initial}` : ''} ${claim.patient_last_name}`
          : claim.status === 'processing' 
            ? '' // Don't show fallback for processing claims
            : `Unknown ${subjectLanguage.singularCap}`,
        patientId: claim.patient_id,
        dateOfService: claim.service_date_from || '',
        dateCreated: claim.created_at || '',
        amount: (claim.total_charge_cents || 0) / 100,
        status: (claim.status || 'draft') as Claim['status'],
        payer: claim.insurance_payer_name || '',
        annotations: claim.annotations || [], // Include annotations from API
      })),
    [claims, subjectLanguage.singularCap],
  );

  // Update polling state based on processing claims
  useEffect(() => {
    const hasProcessing = mappedData.some(claim => claim.status === 'processing');
    setNeedsPolling(hasProcessing);
    
    // Claims processing detection for polling
  }, [mappedData]);

  // Notify parent of selected claims
  useEffect(() => {
    if (onSelectedClaimsChange) {
      const selectedClaims = mappedData.filter(claim => selectedIds.includes(claim.id));
      onSelectedClaimsChange(selectedClaims);
    }
  }, [selectedIds, mappedData, onSelectedClaimsChange]);

  const handleSelectionChange = useCallback((ids: string[]) => {
    setSelectedIds(ids);
  }, []);

  const columns: ColumnDefinition<Claim>[] = useMemo(
    () => [
      // Commented out until API provides real claim numbers
      // {
      //   field: 'claimNumber',
      //   header: 'Claim #',
      //   sortable: true,
      //   width: 140,
      //   priority: 'medium',
      //   mobileHidden: true,
      //   format: (value: unknown) => (
      //     <Typography
      //       variant="body2"
      //       sx={{
      //         fontFamily: 'monospace',
      //         fontSize: '0.875rem',
      //         fontWeight: 500,
      //       }}
      //     >
      //       {value as string}
      //     </Typography>
      //   ),
      // },
      {
        field: 'patientName',
        header: subjectLanguage.singularCap,
        sortable: false, // API doesn't support sorting by subject name
        width: 180,
        sticky: true,
        priority: 'high',
      },
      {
        field: 'payer',
        header: 'Payer',
        sortable: false, // API doesn't support sorting by payer
        width: 160,
        priority: 'medium',
      },
      {
        field: 'dateOfService',
        header: 'Service Date',
        sortable: true,
        width: 120,
        priority: 'high',
        format: (value: unknown) => {
          if (!value) return '-';
          const date = parseISO(value as string);
          return format(date, 'MMM d, yyyy');
        },
      },
      // Commented out until API provides actual submission date (currently shows created_at which is misleading)
      // {
      //   field: 'dateCreated',
      //   header: 'Submitted',
      //   sortable: true,
      //   width: 120,
      //   priority: 'low',
      //   mobileHidden: true,
      //   format: (value: unknown) => {
      //     if (!value) return '-';
      //     const date = parseISO(value as string);
      //     const daysAgo = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
      //     if (daysAgo === 0) return 'Today';
      //     if (daysAgo === 1) return 'Yesterday';
      //     if (daysAgo < 7) return `${daysAgo} days ago`;
      //     return format(date, 'MMM d');
      //   },
      // },
      {
        field: 'amount',
        header: 'Amount',
        sortable: true,
        width: 120,
        align: 'right',
        priority: 'high',
        format: (value: unknown) => (
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          >
            $
            {(value as number).toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Typography>
        ),
      },
      {
        field: 'status',
        header: 'Status',
        sortable: false, // API doesn't support sorting by status
        width: 120,
        priority: 'high',
        format: (value: unknown) => {
          const status = value as ClaimStatus;
          const config = CLAIM_STATUS_OPTIONS.find(opt => opt.value === status);
          
          // Determine icon based on status
          let icon;
          switch (status) {
            case 'draft':
              icon = <EditNoteIcon sx={{ fontSize: 16 }} />;
              break;
            case 'processing':
              icon = <CircularProgress size={16} sx={{ color: 'inherit' }} />;
              break;
            case 'scrubbing':
              icon = <SearchIcon sx={{ fontSize: 16 }} />;
              break;
            case 'finalized':
              icon = <CheckCircleIcon sx={{ fontSize: 16 }} />;
              break;
            default:
              icon = undefined;
          }
          
          return (
            <Chip
              label={config?.label || status}
              size="small"
              color={config?.color || 'default'}
              icon={icon}
              sx={{
                fontSize: '0.75rem',
                height: 24,
                '& .MuiChip-icon': {
                  fontSize: 16,
                },
              }}
            />
          );
        },
      },
      {
        field: 'annotations',
        header: '', // No header title
        sortable: false,
        width: 100,
        priority: 'high',
        format: (_value: unknown, row: Claim) => {
          // Only render if there are annotations
          if (!row.annotations || row.annotations.length === 0) {
            return null;
          }
          return <ClaimAnnotationIndicator annotations={row.annotations} />;
        },
      },
    ],
    [subjectLanguage.singularCap],
  );

  const actions: TableAction<Claim>[] = useMemo(
    () => [
      {
        label: row => submittingClaimId === row.id && isSubmitting ? 'Submitting...' : 'Submit',
        icon: row =>
          submittingClaimId === row.id && isSubmitting ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <SendIcon fontSize="small" />
          ),
        onClick: async row => {
          // Don't allow clicking if already submitting
          if (submittingClaimId === row.id && isSubmitting) {
            return;
          }
          
          // Check if claim has errors
          const hasErrors = row.annotations?.some(a => a.severity === 'error') || false;
          if (hasErrors) {
            enqueueSnackbar('Please fix all errors before submitting', { variant: 'error' });
            return;
          }

          setSubmittingClaimId(row.id);
          const result = await submitClaim(row.id);
          
          if (result.success) {
            if (result.submissionBlocked) {
              // Submission was blocked due to validation errors
              // The panel will automatically open (handled in useClaimSubmit)
              enqueueSnackbar('Submission blocked. Please review and fix validation errors.', { 
                variant: 'warning' 
              });
            } else {
              // Successful submission
              enqueueSnackbar('Claim submitted successfully', { variant: 'success' });
              // Tag invalidation will handle updating the claim status
            }
          } else {
            enqueueSnackbar('Failed to submit claim. Please try again.', { variant: 'error' });
          }
          
          setSubmittingClaimId(null);
        },
        variant: 'contained',
        color: 'primary',
        disabled: row => {
          // Disable if:
          // 1. Claim has error annotations
          // 2. Claim is currently being submitted
          // 3. Claim status is not appropriate for submission
          const hasErrors = row.annotations?.some(a => a.severity === 'error') || false;
          const isCurrentlySubmitting = submittingClaimId === row.id && isSubmitting;
          const isNotSubmittable = ['submitted', 'processing', 'accepted', 'finalized'].includes(row.status);
          
          return hasErrors || isCurrentlySubmitting || isNotSubmittable;
        },
        // Hide submit button for finalized claims
        hidden: row => row.status === 'finalized',
      },
      {
        label: row =>
          row.status === 'finalized' ? 'View' : 'Edit',
        icon: row =>
          row.status === 'finalized' ? (
            <VisibilityIcon fontSize="small" />
          ) : (
            <EditIcon fontSize="small" />
          ),
        onClick: row => {
          if (row.status === 'finalized') {
            // Navigate to view page (read-only for finalized claims)
            navigate(`/billing/claim/${row.id}`);
          } else {
            // Navigate to edit page
            navigate(`/billing/claim/${row.id}/edit`);
          }
        },
      },
      {
        label: row => downloadingClaimIds.has(row.id) ? 'Downloading...' : 'Download PDF',
        icon: row => downloadingClaimIds.has(row.id) 
          ? <CircularProgress size={16} /> 
          : <DownloadIcon fontSize="small" />,
        hidden: row => row.status !== 'finalized',
        disabled: row => downloadingClaimIds.has(row.id),
        onClick: row => {
          void (async () => {
            // Add to downloading set
            setDownloadingClaimIds(prev => new Set(prev).add(row.id));
            try {
              const result = await getClaimPdf({ claimId: row.id }).unwrap();
              // Create blob from base64 data
              const byteCharacters = atob(result.pdf_data);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              const blob = new Blob([byteArray], { type: 'application/pdf' });

              // Download the file
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = result.filename && !result.filename.includes('None') 
                ? result.filename 
                : `claim_${row.id}.pdf`;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);

              enqueueSnackbar('PDF downloaded successfully', { variant: 'success' });
            } catch (error) {
              console.error('Failed to download PDF:', error);
              enqueueSnackbar('Failed to download PDF', { variant: 'error' });
            } finally {
              // Remove from downloading set
              setDownloadingClaimIds(prev => {
                const next = new Set(prev);
                next.delete(row.id);
                return next;
              });
            }
          })();
        },
      },
      {
        label: 'Delete',
        icon: <DeleteIcon fontSize="small" />,
        onClick: row => {
          setClaimToDelete(row);
          setDeleteDialogOpen(true);
        },
        color: 'error',
      },
    ],
    [navigate, getClaimPdf, enqueueSnackbar, submitClaim, isSubmitting, submittingClaimId, downloadingClaimIds],
  );


  // Loading state
  if (isLoading) {
    return (
      <Box>
        <Box sx={{ mb: 2 }}>
          <Skeleton variant="text" width={200} height={24} />
        </Box>
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton key={index} variant="rectangular" height={60} sx={{ mb: 1 }} />
        ))}
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="error" gutterBottom>
          Failed to load claims
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {(error as Error).message || 'An unexpected error occurred'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {totalCount} claim{totalCount !== 1 ? 's' : ''} found
        </Typography>
      </Box>

      <BillingTable
        data={mappedData}
        columns={columns}
        totalItems={totalCount}
        page={page}
        perPage={perPage}
        sortField={sort}
        sortOrder={order}
        onPageChange={onPageChange}
        onPerPageChange={onPerPageChange}
        onSort={onSort}
        onSelectionChange={handleSelectionChange}
        actions={actions}
        emptyMessage="No claims found"
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        type="claim"
        onClose={() => {
          setDeleteDialogOpen(false);
          setClaimToDelete(null);
        }}
        onConfirm={async () => {
          if (claimToDelete) {
            const result = await deleteClaim(claimToDelete.id);
            if (result.success) {
              enqueueSnackbar('Claim deleted successfully', { variant: 'success' });
              refetch();
            } else {
              enqueueSnackbar('Failed to delete claim', { variant: 'error' });
            }
            setDeleteDialogOpen(false);
            setClaimToDelete(null);
          }
        }}
      />
    </Box>
  );
};
